<script setup lang="ts">
import { useI18n } from '@/i18n';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const { t } = useI18n();

const props = defineProps<{
  items: any[];
  interval?: number;
  showControls?: boolean;
  showIndicators?: boolean;
  autoPlay?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:currentIndex', index: number): void;
  (e: 'slideChange', index: number): void;
}>();

const currentIndex = ref(0);
const direction = ref<'next' | 'prev'>('next');
let autoPlayTimerId: number | null = null;

const currentItem = computed(() => props.items[currentIndex.value]);

const next = () => {
  direction.value = 'next';
  currentIndex.value = (currentIndex.value + 1) % props.items.length;
  emit('update:currentIndex', currentIndex.value);
  emit('slideChange', currentIndex.value);
  resetAutoPlay();
};

const prev = () => {
  direction.value = 'prev';
  currentIndex.value = (currentIndex.value - 1 + props.items.length) % props.items.length;
  emit('update:currentIndex', currentIndex.value);
  emit('slideChange', currentIndex.value);
  resetAutoPlay();
};

const goTo = (index: number) => {
  direction.value = index > currentIndex.value ? 'next' : 'prev';
  currentIndex.value = index;
  emit('update:currentIndex', currentIndex.value);
  emit('slideChange', currentIndex.value);
  resetAutoPlay();
};

// Watch for changes in currentIndex and emit slideChange
watch(currentIndex, (newIndex) => {
  emit('slideChange', newIndex);
});

const resetAutoPlay = () => {
  stopAutoPlay();
  startAutoPlay();
};

const startAutoPlay = () => {
  if (props.autoPlay && props.interval) {
    autoPlayTimerId = window.setInterval(next, props.interval);
  }
};

const stopAutoPlay = () => {
  if (autoPlayTimerId) {
    clearInterval(autoPlayTimerId);
    autoPlayTimerId = null;
  }
};

onMounted(() => {
  startAutoPlay();
  // Emit initial slide number
  emit('slideChange', currentIndex.value);
});

onUnmounted(() => {
  stopAutoPlay();
});
</script>

<template>
  <div class="carousel">
    <div class="carousel-inner">
      <slot name="stars" :item="currentItem" />
      <Transition :name="direction" mode="out-in">
        <div :key="currentIndex" class="carousel-item">
          <slot :item="currentItem" :index="currentIndex" />
        </div>
      </Transition>
    </div>

    <div v-if="showControls" class="carousel-controls">
      <button class="control prev" :aria-label="t('carousel.aria_previous')" @click="prev">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </button>
      <button class="control next" :aria-label="t('carousel.aria_next')" @click="next">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
      </button>
    </div>

    <div v-if="showIndicators" class="carousel-indicators">
      <button
        v-for="(_, index) in items"
        :key="index"
        class="indicator"
        :class="{ active: index === currentIndex }"
        :aria-label="t('carousel.aria_go_to_slide', { n: index + 1 })"
        @click="goTo(index)"
      />
    </div>
  </div>
</template>

<style scoped>
.carousel {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.carousel-inner {
  width: 100%;
  height: 100%;
  position: relative;
}

.carousel-item {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

/* Slide animations */
.next-enter-active,
.next-leave-active,
.prev-enter-active,
.prev-leave-active {
  transition: all 0.5s ease;
}

.next-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.next-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.prev-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.prev-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.carousel-controls {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 var(--space-small);
  pointer-events: none;
  z-index: 2;
}

.control {
  background: var(--color-bg-blur);
  border: none;
  border-radius: 50%;
  width: var(--size-xxlarge);
  height: var(--size-xxlarge);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: background-color 0.2s ease;
}

.control:hover {
  background: color-mix(in srgb, var(--color-fg-app) 92%, transparent);
}

@supports not (background: color-mix(in srgb, red 50%, blue)) {
  .control:hover {
    background: var(--color-bg-blur);
  }
}

.control svg {
  width: var(--size-medium);
  height: var(--size-medium);
  color: var(--color-text-primary);
}

.carousel-indicators {
  position: absolute;
  bottom: var(--space-xtiny);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: var(--size-tiny);
  z-index: 2;
}

.indicator {
  width: var(--size-tiny);
  height: var(--size-tiny);
  border-radius: 50%;
  background: var(--color-border-interactive);
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.indicator.active {
  background: var(--color-text-primary);
}

.indicator:hover {
  background: color-mix(in srgb, var(--color-text-primary) 70%, transparent);
}

@supports not (background: color-mix(in srgb, red 50%, blue)) {
  .indicator:hover {
    background: var(--color-text-secondary);
  }
}
</style>
